// /settings → "Нэгтгэх дүрэм" tab (A3) with §9.5/B4 inline CRUD modal.

import { Badge, Box, Group, Stack, Table, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ConsolidationRuleApi } from "../../apis";
import type { IConsolidationRule } from "../../apis/consolidation-rule";
import { ListState } from "../ui";
import { SETTINGS_WRITE, SettingsActionCell, SettingsButton, SettingsRowAction } from "./settings-controls";
import RenderText from "../ui/render-text";
import { ConsolidationRuleFormModal } from "./ConsolidationRuleFormModal";
import { useSettingsMutations } from "./hooks";
import { DotPill } from "../delivery/atoms"
import { EmptyStateGuide } from "@dehub/theme"
import { HelpNote } from "../ui"

export function ConsolidationRuleTab() {
  const { t } = useTranslation("settings");
  const { data, isLoading, error, mutate: reload } = useSWR(
    "settings.consolidation-rule.list",
    () => ConsolidationRuleApi.list({ offset: { limit: 50, skip: 0 } }),
    { revalidateOnFocus: false },
  );
  const rows = data?.rows ?? [];
  const mutations = useSettingsMutations();

  const [editTarget, setEditTarget] = useState<IConsolidationRule | null>(null);
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const handleEdit = (rule: IConsolidationRule) => {
    setEditTarget(rule);
    openEdit();
  };

  const handleDelete = (rule: IConsolidationRule) => {
    modals.openConfirmModal({
      title: t("common.confirmDelete", { name: rule.name }),
      children: (
        <Text size="sm">
          <Text span fw={600}>{rule.name}</Text>{" "}
          {t("common.confirmDelete", { name: "" }).replace("?", "")}?
          <HelpNote helpKey="order.settings.confirm.delete-consolidation" />
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => mutations.consolidationRule.remove(rule.id),
    });
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Stack gap={2}>
          <Text fw={600}>{t("consolidation.title")}</Text>
        </Stack>
        <SettingsButton code={SETTINGS_WRITE.governance} helpKey="order.settings.action.new-consolidation" helpId="settings-new-consolidation" leftSection={<PlusIcon size={18} />} onClick={openCreate}>
          {t("consolidation.actions.newRule")}
        </SettingsButton>
      </Group>

      {isLoading ? (
        <ListState state="loading" columns={7} />
      ) : error ? (
        <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reload()} />
      ) : rows.length === 0 ? (
        <EmptyStateGuide helpKey="order.settings.empty.main" when="consolidation" actions={{ create: openCreate }} fallback={<ListState state="empty" title={t("common.empty")} description={t("common.emptyHint")} />} />
      ) : (
        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={120}>{t("common.actions")}</Table.Th>
              <Table.Th>{t("consolidation.fields.name.label")}</Table.Th>
              <Table.Th>{t("consolidation.table.bucketKey")}</Table.Th>
              <Table.Th>{t("consolidation.fields.maxOrdersPerBucket.label")}</Table.Th>
              <Table.Th>{t("consolidation.table.sameDay")}</Table.Th>
              <Table.Th>{t("consolidation.table.autoRun")}</Table.Th>
              <Table.Th>{t("common.active")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <SettingsActionCell>
                    <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<PencilSimpleIcon />} label={t("common.edit")} onClick={() => handleEdit(r)} />
                    <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<TrashIcon />} label={t("common.delete")} onClick={() => handleDelete(r)} />
                  </SettingsActionCell>
                </Table.Td>
                <Table.Td><RenderText text={r.name} fontWeight={500} /></Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {(r.bucketKeyComponents ?? []).map((c) => (
                      <Badge key={c} size="lg" variant="light" color="gray" tt="none">
                        {t(`consolidation.options.bucketKey.${c}`, { defaultValue: "-" })}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td><RenderText text={String(r.maxOrdersPerBucket ?? "-")} /></Table.Td>
                <Table.Td>
                  <Badge size="lg" variant="light" color={r.sameDayOnly ? "blue" : "gray"}>
                    {r.sameDayOnly ? t("common.yes") : t("common.no")}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="lg" variant="light" color={r.autoRunEnabled ? "blue" : "gray"}>
                    {r.autoRunEnabled ? t("common.yes") : t("common.no")}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <DotPill tone={r.isActive ? "green" : "grey"}>
                    {r.isActive ? t("common.active") : t("common.inactive")}
                  </DotPill>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <ConsolidationRuleFormModal
        opened={createOpen}
        onClose={closeCreate}
      />
      <ConsolidationRuleFormModal
        opened={editOpen}
        onClose={closeEdit}
        initial={editTarget}
        key={`edit-${editTarget?.id ?? "none"}`}
      />
    </Box>
  );
}
